import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import './ivyMark.css';

export type IvyMarkState = 'idle' | 'working' | 'streaming' | 'done' | 'needs-you' | 'error';

export interface IvyMarkProps {
  state?: IvyMarkState;
  /** Changes on each streamed chunk (the text's length will do): her mouth moves while she streams. */
  pulse?: number | string;
  /** No motion at all: every turn but the newest. */
  still?: boolean;
  size?: number;
  className?: string;
}

const BLINKS: IvyMarkState[] = ['idle', 'streaming', 'needs-you'];
const BLINK_MS = 140;
const SAY_MS = 100;
const SHUT_MS = 90;
/** The done hop runs in the first ~1.2 s of its animation; after this the mark is idle again. */
const DONE_MS = 1600;

const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/**
 * Ivy's mark (Studio B): the 4K Labs tile with a face. Each chat state is a pose (CSS, keyed on data-state); this
 * component adds what CSS can't time: blinks, the mouth on each streamed chunk, and done settling back to idle.
 */
export function IvyMark({ state = 'idle', pulse, still = false, size = 28, className }: IvyMarkProps) {
  const shown = useSettled(state, still);
  const blink = useBlink(!still && BLINKS.includes(shown));
  const say = useSay(pulse, !still && shown === 'streaming');
  return (
    <svg
      data-ivy-mark
      data-state={shown}
      data-still={still || undefined}
      data-blink={blink || undefined}
      data-say={say || undefined}
      aria-hidden
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={cn('ivy-mark shrink-0', className)}
    >
      <title>Ivy</title>
      <g className="im-body">
        <rect className="im-tile" x="4" y="4" width="56" height="56" rx="16" />
        <path className="im-sheen" d="M13 22c0-6 3-9 9-9" />
        <g className="im-look">
          <g className="im-f im-open im-face">
            <rect className="im-eye" x="19" y="20" width="8" height="15" rx="4" />
            <rect className="im-eye" x="37" y="20" width="8" height="15" rx="4" />
          </g>
        </g>
        <g className="im-f im-brows im-line" strokeWidth="3.5">
          <path d="M18 13.5l9-1.5" />
          <path d="M46 13.5l-9-1.5" />
        </g>
        <g className="im-f im-chev im-line" strokeWidth="5">
          <path className="im-c1" d="M19 20l8 8-8 8" />
          <path className="im-c2" d="M35 20l8 8-8 8" />
        </g>
        <g className="im-f im-happy im-line" strokeWidth="5">
          <path d="M18 31q5-9 10 0" />
          <path d="M36 31q5-9 10 0" />
          <path d="M25 42q7 6 14 0" />
        </g>
        <g className="im-f im-sad">
          <g className="im-face">
            <rect x="19" y="26" width="8" height="10" rx="4" />
            <rect x="37" y="26" width="8" height="10" rx="4" />
          </g>
          <g className="im-line" strokeWidth="3.5">
            <path d="M17 22l10-4" />
            <path d="M47 22l-10-4" />
            <path d="M26 46q6-4 12 0" />
          </g>
        </g>
        <rect className="im-mouth im-face" x="26" y="41" width="12" height="8" rx="4" />
      </g>
      <g className="im-f im-badge">
        <circle cx="56" cy="8" r="8" />
        <path d="M56 4.5v4.5M56 12v.2" />
      </g>
    </svg>
  );
}

/**
 * The state to show: `done` shows its hop, then idle, without the caller keeping a timer. Only a change to done hops:
 * a mark that first appears done (a finished thread, opened again) is idle.
 */
function useSettled(state: IvyMarkState, still: boolean): IvyMarkState {
  const [settled, setSettled] = useState(state === 'done');
  const [prev, setPrev] = useState(state);
  if (prev !== state) {
    setPrev(state);
    setSettled(false);
  }
  useEffect(() => {
    if (state !== 'done' || still) return;
    const t = setTimeout(() => setSettled(true), DONE_MS);
    return () => clearTimeout(t);
  }, [state, still]);
  return settled && state === 'done' ? 'idle' : state;
}

function useBlink(on: boolean): boolean {
  const [blink, setBlink] = useState(false);
  useEffect(() => {
    if (!on || reducedMotion()) return;
    let t: ReturnType<typeof setTimeout>;
    const next = (ms: number) => {
      t = setTimeout(() => {
        setBlink(true);
        t = setTimeout(() => {
          setBlink(false);
          next(Math.random() < 0.18 ? 260 : 2600 + Math.random() * 3000);
        }, BLINK_MS);
      }, ms);
    };
    next(600 + Math.random() * 2400);
    return () => {
      clearTimeout(t);
      setBlink(false);
    };
  }, [on]);
  return blink;
}

/** Open on a new pulse, shut after SAY_MS, then ignore pulses for SHUT_MS so fast chunks don't turn into a blur. */
function useSay(pulse: IvyMarkProps['pulse'], on: boolean): boolean {
  const [say, setSay] = useState(false);
  const last = useRef(pulse);
  const busy = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (pulse === last.current) return;
    last.current = pulse;
    if (!on || busy.current || reducedMotion()) return;
    setSay(true);
    busy.current = setTimeout(() => {
      setSay(false);
      busy.current = setTimeout(() => {
        busy.current = undefined;
      }, SHUT_MS);
    }, SAY_MS);
  }, [pulse, on]);
  useEffect(() => () => clearTimeout(busy.current), []);
  return say;
}
